import React, { useState, useEffect } from 'react';
import { tenantAdminAPI } from '../../../services/api/tenantAdminAPI';
import toast from 'react-hot-toast';
import { 
  Settings, 
  Save, 
  RefreshCw, 
  Bell, 
  Shield, 
  Globe, 
  Clock, 
  Database,
  Mail,
  Phone,
  MapPin,
  Building2
} from 'lucide-react';

const OrganizationSettings = () => {
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    email: '',
    phone: '',
    website: '',
    operating_hours: '',
    // Notification settings
    email_notifications: true,
    sms_notifications: false,
    system_alerts: true,
    // Security settings
    two_factor_auth: true,
    session_timeout: '30',
    // Integration settings
    api_access: false,
    webhook_url: ''
  });

  useEffect(() => {
    loadTenantInfo();
  }, []);

  const loadTenantInfo = async () => {
    try {
      setLoading(true);
      const response = await tenantAdminAPI.getTenantInfo();
      setTenantInfo(response.data);
      
      // Populate form with existing data
      setFormData(prev => ({
        ...prev,
        name: response.data.name || '',
        description: response.data.description || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        website: response.data.website || '',
        operating_hours: response.data.operating_hours || ''
      }));
    } catch (error) {
      console.error('Failed to load tenant info:', error);
      toast.error('Failed to load organization information');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await tenantAdminAPI.updateTenant(formData);
      toast.success('Settings updated successfully');
      loadTenantInfo(); // Reload to get updated data
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'integrations', label: 'Integrations', icon: Globe }
  ];

  const GeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[#0B1C2C] mb-4">Organization Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
              Organization Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
              placeholder="Enter organization name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
              placeholder="organization@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
              Website
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
              placeholder="https://www.example.com"
            />
          </div>
        </div>

        {/* <div className="mt-6">
          <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
            placeholder="Brief description of your organization"
          />
        </div> */}

        {/* <div className="mt-6">
          <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
            Operating Hours
          </label>
          <input
            type="text"
            value={formData.operating_hours}
            onChange={(e) => setFormData({ ...formData, operating_hours: e.target.value })}
            className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
            placeholder="e.g., Monday-Friday 9:00 AM - 5:00 PM"
          />
        </div> */}
      </div>

      {tenantInfo && (
        <div className="bg-[#DCEAF5] p-4 rounded-lg">
          <h4 className="text-sm font-medium text-[#0B1C2C] mb-2">Organization Details</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-[#3D3D3D]">Organization ID:</span>
              <span className="ml-2 font-mono text-[#0B1C2C]">{tenantInfo.id}</span>
            </div>
            <div>
              <span className="text-[#3D3D3D]">Type:</span>
              <span className="ml-2 text-[#0B1C2C]">{tenantInfo.tenant_type || 'Not specified'}</span>
            </div>
            <div>
              <span className="text-[#3D3D3D]">Created:</span>
              <span className="ml-2 text-[#0B1C2C]">
                {tenantInfo.created_at ? new Date(tenantInfo.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            <div>
              <span className="text-[#3D3D3D]">Status:</span>
              <span className="ml-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#DCEAF5] text-[#008080]">
                  {tenantInfo.status || 'Active'}
                </span>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const NotificationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[#0B1C2C] mb-4">Notification Preferences</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-[#DCEAF5] rounded-lg">
            <div className="flex items-start space-x-3">
              <Mail className="h-5 w-5 text-[#3D3D3D] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-[#0B1C2C]">Email Notifications</h4>
                <p className="text-sm text-[#3D3D3D]">Receive email alerts for important updates</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.email_notifications}
                onChange={(e) => setFormData({ ...formData, email_notifications: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[#DCEAF5] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#207CE5] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#DCEAF5] after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#207CE5]"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 border border-[#DCEAF5] rounded-lg">
            <div className="flex items-start space-x-3">
              <Phone className="h-5 w-5 text-[#3D3D3D] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-[#0B1C2C]">SMS Notifications</h4>
                <p className="text-sm text-[#3D3D3D]">Receive text messages for urgent alerts</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.sms_notifications}
                onChange={(e) => setFormData({ ...formData, sms_notifications: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[#DCEAF5] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#207CE5] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#DCEAF5] after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#207CE5]"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 border border-[#DCEAF5] rounded-lg">
            <div className="flex items-start space-x-3">
              <Bell className="h-5 w-5 text-[#3D3D3D] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-[#0B1C2C]">System Alerts</h4>
                <p className="text-sm text-[#3D3D3D]">Get notified about system maintenance and updates</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.system_alerts}
                onChange={(e) => setFormData({ ...formData, system_alerts: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[#DCEAF5] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#207CE5] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#DCEAF5] after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#207CE5]"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const SecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[#0B1C2C] mb-4">Security Settings</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-[#DCEAF5] rounded-lg">
            <div className="flex items-start space-x-3">
              <Shield className="h-5 w-5 text-[#3D3D3D] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-[#0B1C2C]">Two-Factor Authentication</h4>
                <p className="text-sm text-[#3D3D3D]">Add an extra layer of security to user accounts</p>
              </div>
            </div>
            <div className="relative inline-flex items-center">
              <input
                type="checkbox"
                checked={true}
                disabled={true}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[#207CE5] rounded-full cursor-not-allowed opacity-60">
                <div className="w-5 h-5 bg-white rounded-full border border-[#DCEAF5] mt-0.5 ml-5"></div>
              </div>
            </div>
          </div>

          <div className="p-4 border border-[#DCEAF5] rounded-lg">
            <div className="flex items-start space-x-3 mb-3">
              <Clock className="h-5 w-5 text-[#3D3D3D] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-[#0B1C2C]">Session Timeout</h4>
                <p className="text-sm text-[#3D3D3D] mt-1">Automatically log out users after a period of inactivity</p>
              </div>
            </div>
            <select
              value={formData.session_timeout}
              onChange={(e) => setFormData({ ...formData, session_timeout: e.target.value })}
              className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
            >
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
              <option value="240">4 hours</option>
              <option value="480">8 hours</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const IntegrationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[#0B1C2C] mb-4">Integration Settings</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-[#DCEAF5] rounded-lg">
            <div className="flex items-start space-x-3">
              <Database className="h-5 w-5 text-[#3D3D3D] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-[#0B1C2C]">API Access</h4>
                <p className="text-sm text-[#3D3D3D]">Enable API access for third-party integrations</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.api_access}
                onChange={(e) => setFormData({ ...formData, api_access: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[#DCEAF5] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#207CE5] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#DCEAF5] after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#207CE5]"></div>
            </label>
          </div>

          <div className="p-4 border border-[#DCEAF5] rounded-lg">
            <div className="flex items-start space-x-3 mb-3">
              <Globe className="h-5 w-5 text-[#3D3D3D] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-[#0B1C2C]">Webhook URL</h4>
                <p className="text-sm text-[#3D3D3D] mt-1">Receive real-time notifications about system events</p>
              </div>
            </div>
            <input
              type="url"
              value={formData.webhook_url}
              onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
              className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
              placeholder="https://your-system.com/webhook"
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0B1C2C]">Organization Settings</h1>
          <p className="text-[#3D3D3D]">Manage your organization preferences and configuration</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadTenantInfo}
            className="flex items-center space-x-2 px-4 py-2 border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5]"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#4682B4] disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>

      <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-[#DCEAF5]">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-[#207CE5] text-[#207CE5]'
                      : 'border-transparent text-[#3D3D3D] hover:text-[#0B1C2C] hover:border-[#DCEAF5]'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'general' && <GeneralSettings />}
          {activeTab === 'notifications' && <NotificationSettings />}
          {activeTab === 'security' && <SecuritySettings />}
          {activeTab === 'integrations' && <IntegrationSettings />}
        </div>
      </div>
    </div>
  );
};

export default OrganizationSettings;